// Private requests and mounted session gates share this lifetime. Network
// completion from a previous identity must never populate the new cache.
let state = { generation: 0, changing: false, storeSlug: undefined as string | undefined, actorId: undefined as string | undefined };
const listeners = new Set<() => void>();
export const getPrivateSessionState = () => state;
export function subscribePrivateSession(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
function emit() { for (const listener of listeners) listener(); }
export function beginPrivateSessionChange() {
  if (state.changing) throw new DOMException("Session change in progress", "AbortError");
  state = { ...state, generation: state.generation + 1, changing: true };
  emit();
}
export function finishPrivateSessionChange(storeSlug?: string, actorId?: string) {
  state = { ...state, changing: false, storeSlug, actorId };
  emit();
}
export function assertPrivateSession(generation: number) {
  if (state.changing || state.generation !== generation) {
    throw new DOMException("Private session changed", "AbortError");
  }
}

/** A retained callback belongs to the private identity that created it. */
export type PrivateSessionLifetime = { generation: number; storeSlug?: string };
export function capturePrivateSession(storeSlug?: string): PrivateSessionLifetime {
  return { generation: state.generation, storeSlug };
}
export function isPrivateSessionCurrent(lifetime: PrivateSessionLifetime): boolean {
  return !state.changing && state.generation === lifetime.generation &&
    (!lifetime.storeSlug || lifetime.storeSlug === state.storeSlug);
}
export function assertPrivateSessionLifetime(lifetime: PrivateSessionLifetime) {
  if (!isPrivateSessionCurrent(lifetime)) {
    throw new DOMException("Private session changed", "AbortError");
  }
}

/** Only a successful session inspection establishes the initial cookie binding. */
export function establishPrivateSessionBinding(generation: number, storeSlug: string, actorId?: string) {
  assertPrivateSession(generation);
  if ((state.storeSlug && state.storeSlug !== storeSlug) || (state.actorId && state.actorId !== actorId)) {
    throw new DOMException("Private store binding changed", "AbortError");
  }
  if (!state.storeSlug || !state.actorId) {
    state = { ...state, storeSlug, actorId };
    emit();
  }
}

/** External cookie changes invalidate retained callbacks even during another transition. */
export function invalidatePrivateSession() {
  state = { ...state, generation: state.generation + 1, changing: true };
  emit();
}
